import { useEffect, useState } from "react";
import { Plus, Trash2, Receipt, Paperclip, TrendingDown, ShoppingBag } from "lucide-react";
import { api, apiError, descargarFacturaPdf } from "@/lib/api";
import { money, fecha, norm } from "@/lib/format";
import { Loading, PageTitle, Stat } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LineasVenta, lineaVacia, hayFaltantes } from "@/components/LineasVenta";
import { BuscadorEstudiante } from "@/components/BuscadorEstudiante";
import { toast } from "sonner";

const hoy = () => new Date().toISOString().slice(0, 10);

const gastoVacio = {
  fecha: hoy(), tipo: "diario", categoria: "", descripcion: "", monto: "",
  proveedor: "", metodo_pago: "efectivo", comprobante_nombre: null, comprobante_base64: null,
};
const ventaVacia = {
  fecha: hoy(), metodo_pago: "efectivo", estudiante_id: "", facturar: false, fiscal: true,
  ncf_manual: false, ncf: "", permitir_negativo: false, lineas: [{ ...lineaVacia }],
};

export default function AdminGastos() {
  const [gastos, setGastos] = useState(null);
  const [ventas, setVentas] = useState([]);
  const [catGasto, setCatGasto] = useState([]);
  const [estudiantes, setEstudiantes] = useState([]);
  const [gasto, setGasto] = useState(null);
  const [venta, setVenta] = useState(null);
  const [facturarVenta, setFacturarVenta] = useState(null);
  const [ncfSugerido, setNcfSugerido] = useState("");
  const [articulos, setArticulos] = useState([]);
  const [busqueda, setBusqueda] = useState("");

  const cargar = () => {
    api.get("/gastos").then((r) => setGastos(r.data)).catch(() => setGastos([]));
    api.get("/ventas").then((r) => setVentas(r.data));
    api.get("/categorias", { params: { tipo: "gasto" } }).then((r) => setCatGasto(r.data));
    api.get("/estudiantes").then((r) => setEstudiantes(r.data));
    api.get("/inventario").then((r) => setArticulos(r.data.articulos)).catch(() => setArticulos([]));
    api.get("/ncf/siguiente").then((r) => setNcfSugerido(r.data.sugerido)).catch(() => {});
  };

  useEffect(cargar, []);

  const subirComprobante = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setGasto((g) => ({
      ...g, comprobante_nombre: file.name, comprobante_base64: String(reader.result),
    }));
    reader.readAsDataURL(file);
  };

  const guardarGasto = async () => {
    if (!gasto.categoria) return toast.error("Seleccione la categoría del gasto");
    if (!gasto.descripcion.trim()) return toast.error("Describa el gasto");
    if (!gasto.monto || Number(gasto.monto) <= 0) return toast.error("Indique un monto mayor a cero");
    const payload = { ...gasto, monto: Number(gasto.monto), fecha: new Date(gasto.fecha).toISOString() };
    try {
      if (gasto.id) await api.put(`/gastos/${gasto.id}`, payload);
      else await api.post("/gastos", payload);
      toast.success(gasto.id ? "Gasto actualizado" : "Gasto registrado");
      setGasto(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarVenta = async () => {
    const lineas = venta.lineas.filter((l) => l.articulo_id || l.descripcion.trim());
    if (lineas.length === 0) return toast.error("Agregue un artículo o describa la venta");
    if (lineas.some((l) => !l.cantidad || Number(l.cantidad) <= 0)) {
      return toast.error("Indique la cantidad de cada línea");
    }
    if (lineas.some((l) => !l.precio_unitario || Number(l.precio_unitario) <= 0)) {
      return toast.error("Indique el precio de cada línea");
    }
    if (venta.facturar && !venta.estudiante_id) return toast.error("Seleccione el estudiante para facturar");
    if (venta.facturar && venta.ncf_manual && !venta.ncf.trim()) {
      return toast.error("Escriba el número de comprobante fiscal");
    }
    try {
      const { data } = await api.post("/ventas", {
        fecha: new Date(venta.fecha).toISOString(),
        metodo_pago: venta.metodo_pago,
        estudiante_id: venta.estudiante_id || null,
        facturar: venta.facturar,
        fiscal: venta.fiscal,
        ncf: venta.facturar && venta.ncf_manual ? venta.ncf.trim() : null,
        permitir_negativo: venta.permitir_negativo,
        lineas: lineas.map((l) => ({
          articulo_id: l.articulo_id || null,
          descripcion: l.descripcion.trim() || null,
          categoria: l.categoria || null,
          cantidad: Number(l.cantidad),
          precio_unitario: Number(l.precio_unitario),
        })),
      });
      toast.success(data.factura ? `Venta registrada y factura ${data.factura.ncf} emitida` : "Venta registrada",
        { description: data.aviso_existencia ? `Existencia en negativo: ${data.aviso_existencia.join(" · ")}` : undefined });
      setVenta(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const emitirFacturaVenta = async () => {
    const f = facturarVenta;
    if (!f.estudiante_id) return toast.error("Seleccione el estudiante al que se factura la venta");
    if (f.ncf_manual && !f.ncf.trim()) return toast.error("Escriba el número de comprobante fiscal");
    try {
      const { data } = await api.post(`/ventas/${f.venta.id}/facturar`, {
        estudiante_id: f.estudiante_id,
        ncf: f.ncf_manual ? f.ncf.trim() : null,
      });
      toast.success(`Factura ${data.factura.ncf} emitida por la venta`);
      setFacturarVenta(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (tipo, id) => {
    await api.delete(`/${tipo}/${id}`);
    toast.success("Registro eliminado");
    cargar();
  };

  const verComprobante = async (g) => {
    try {
      const { data } = await api.get(`/gastos/${g.id}/comprobante`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el comprobante"));
    }
  };

  if (!gastos) return <Loading />;

  const q = norm(busqueda.trim());
  const gastosVis = gastos.filter((g) => !q || [g.descripcion, g.categoria, g.proveedor].filter(Boolean).some((v) => norm(v).includes(q)));
  const fijos = gastos.filter((g) => g.tipo === "fijo");
  const diarios = gastos.filter((g) => g.tipo === "diario");

  return (
    <div data-testid="admin-gastos">
      <PageTitle title="Gastos y ventas"
                 subtitle="Gastos fijos y diarios del colegio con comprobantes adjuntos, y ventas de uniformes, libros y útiles." />

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="gastos-fijos-total" label="Gastos fijos" value={money(fijos.reduce((s, g) => s + g.monto, 0))}
              icon={TrendingDown} hint={`${fijos.length} registro(s)`} />
        <Stat testId="gastos-diarios-total" label="Gastos diarios" value={money(diarios.reduce((s, g) => s + g.monto, 0))}
              tone="warn" hint={`${diarios.length} registro(s)`} />
        <Stat testId="ventas-total" label="Ventas" value={money(ventas.reduce((s, v) => s + v.total, 0))}
              tone="good" icon={ShoppingBag} hint={`${ventas.length} venta(s)`} />
        <Stat testId="gastos-con-comprobante" label="Con comprobante"
              value={`${gastos.filter((g) => g.comprobante_nombre).length}/${gastos.length}`}
              icon={Paperclip} hint="Gastos justificados" />
      </div>

      <Tabs defaultValue="gastos" className="mt-8">
        <TabsList>
          <TabsTrigger value="gastos" data-testid="tab-gastos">Gastos</TabsTrigger>
          <TabsTrigger value="ventas" data-testid="tab-ventas">Ventas</TabsTrigger>
        </TabsList>

        <TabsContent value="gastos" className="mt-6">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Input className="max-w-xs rounded-full" placeholder="Buscar gasto…" value={busqueda}
                   onChange={(e) => setBusqueda(e.target.value)} data-testid="buscar-gasto" />
            <Button className="rounded-full" onClick={() => setGasto({ ...gastoVacio, categoria: catGasto[0]?.nombre || "" })}
                    data-testid="nuevo-gasto">
              <Plus className="mr-2 h-4 w-4" /> Registrar gasto
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm" data-testid="tabla-gastos">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Fecha</th>
                    <th className="px-6 py-3">Tipo</th>
                    <th className="px-6 py-3">Categoría</th>
                    <th className="px-6 py-3">Descripción</th>
                    <th className="px-6 py-3">Proveedor</th>
                    <th className="px-6 py-3">Monto</th>
                    <th className="px-6 py-3">Comprobante</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {gastosVis.map((g) => (
                    <tr key={g.id} className="border-t border-border/60 hover:bg-muted/30">
                      <td className="px-6 py-4 font-mono text-xs">{fecha(g.fecha)}</td>
                      <td className="px-6 py-4">
                        <Badge variant={g.tipo === "fijo" ? "secondary" : "outline"}>{g.tipo}</Badge>
                      </td>
                      <td className="px-6 py-4">{g.categoria}</td>
                      <td className="px-6 py-4">{g.descripcion}</td>
                      <td className="px-6 py-4 text-muted-foreground">{g.proveedor || "—"}</td>
                      <td className="px-6 py-4 font-mono font-semibold">{money(g.monto)}</td>
                      <td className="px-6 py-4">
                        {g.comprobante_nombre ? (
                          <button onClick={() => verComprobante(g)} data-testid={`ver-comprobante-${g.id}`}
                                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                            <Paperclip className="h-3 w-3" /> {g.comprobante_nombre.slice(0, 18)}
                          </button>
                        ) : <span className="text-xs text-muted-foreground">sin adjunto</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="rounded-full"
                                  onClick={() => setGasto({ ...g, fecha: g.fecha.slice(0, 10), monto: String(g.monto) })}
                                  data-testid={`editar-gasto-${g.id}`}>Editar</Button>
                          <Button size="sm" variant="ghost" onClick={() => eliminar("gastos", g.id)}
                                  data-testid={`eliminar-gasto-${g.id}`}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {gastosVis.length === 0 && (
              <p className="p-10 text-center text-sm text-muted-foreground" data-testid="gastos-vacios">
                No hay gastos registrados.
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ventas" className="mt-6">
          <Button className="mb-6 rounded-full" onClick={() => setVenta({ ...ventaVacia, lineas: [{ ...lineaVacia }] })}
                  data-testid="nueva-venta">
            <Plus className="mr-2 h-4 w-4" /> Registrar venta
          </Button>
          <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-sm" data-testid="tabla-ventas">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Fecha</th>
                    <th className="px-6 py-3">Categoría</th>
                    <th className="px-6 py-3">Descripción</th>
                    <th className="px-6 py-3">Cant.</th>
                    <th className="px-6 py-3">Total</th>
                    <th className="px-6 py-3">Estudiante</th>
                    <th className="px-6 py-3">Factura</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.map((v) => (
                    <tr key={v.id} className="border-t border-border/60 hover:bg-muted/30">
                      <td className="px-6 py-4 font-mono text-xs">{fecha(v.fecha)}</td>
                      <td className="px-6 py-4">{v.categoria}</td>
                      <td className="px-6 py-4">
                        {v.descripcion}
                        {(v.lineas?.length || 0) > 1 && (
                          <span className="mt-1 block text-xs text-muted-foreground" data-testid={`lineas-venta-${v.id}`}>
                            {v.lineas.map((l) => `${l.cantidad} × ${l.descripcion}`).join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-mono">{v.cantidad}</td>
                      <td className="px-6 py-4 font-mono font-semibold">{money(v.total)}</td>
                      <td className="px-6 py-4 text-muted-foreground">{v.estudiante_nombre || "—"}</td>
                      <td className="px-6 py-4">
                        {v.factura_ncf ? (
                          <button onClick={() => descargarFacturaPdf(v.factura_id, v.factura_ncf)}
                                  data-testid={`ver-factura-venta-${v.id}`}
                                  className="flex items-center gap-1 font-mono text-xs text-primary hover:underline">
                            <Receipt className="h-3 w-3" /> {v.factura_ncf}
                          </button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-full text-xs"
                                  data-testid={`facturar-venta-${v.id}`}
                                  onClick={() => setFacturarVenta({
                                    venta: v, estudiante_id: v.estudiante_id || "", ncf_manual: false, ncf: "" })}>
                            <Receipt className="mr-1 h-3 w-3" /> Emitir factura
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => eliminar("ventas", v.id)}
                                data-testid={`eliminar-venta-${v.id}`}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ventas.length === 0 && (
              <p className="p-10 text-center text-sm text-muted-foreground" data-testid="ventas-vacias">
                No hay ventas registradas.
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!gasto} onOpenChange={(o) => !o && setGasto(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-gasto">
          <DialogHeader>
            <DialogTitle className="font-display">{gasto?.id ? "Editar gasto" : "Registrar gasto"}</DialogTitle>
            <DialogDescription>Adjunte la factura o recibo para justificar el gasto.</DialogDescription>
          </DialogHeader>
          {gasto && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={gasto.fecha} data-testid="gasto-fecha"
                       onChange={(e) => setGasto({ ...gasto, fecha: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={gasto.tipo} onChange={(e) => setGasto({ ...gasto, tipo: e.target.value })}
                        data-testid="gasto-tipo">
                  <option value="fijo">Gasto fijo</option>
                  <option value="diario">Gasto diario</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={gasto.categoria} onChange={(e) => setGasto({ ...gasto, categoria: e.target.value })}
                        data-testid="gasto-categoria">
                  <option value="">Seleccione…</option>
                  {catGasto.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Monto (RD$)</Label>
                <Input type="number" value={gasto.monto} data-testid="gasto-monto"
                       onChange={(e) => setGasto({ ...gasto, monto: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Descripción</Label>
                <Input value={gasto.descripcion} data-testid="gasto-descripcion"
                       onChange={(e) => setGasto({ ...gasto, descripcion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Input value={gasto.proveedor || ""} data-testid="gasto-proveedor"
                       onChange={(e) => setGasto({ ...gasto, proveedor: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Método de pago</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={gasto.metodo_pago} onChange={(e) => setGasto({ ...gasto, metodo_pago: e.target.value })}>
                  {["efectivo", "transferencia", "tarjeta", "cheque"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Comprobante (factura, recibo o foto)</Label>
                <Input type="file" onChange={subirComprobante} data-testid="gasto-comprobante" />
                {gasto.comprobante_nombre && (
                  <p className="text-xs text-primary">Adjunto: {gasto.comprobante_nombre}</p>
                )}
              </div>
              <Button className="sm:col-span-2 rounded-full py-6" onClick={guardarGasto} data-testid="guardar-gasto">
                Guardar gasto
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!venta} onOpenChange={(o) => !o && setVenta(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-venta">
          <DialogHeader>
            <DialogTitle className="font-display">Registrar venta</DialogTitle>
            <DialogDescription>
              Uniformes, libros y útiles. Al elegir un artículo del inventario se toma su precio y se
              descuenta la existencia.
            </DialogDescription>
          </DialogHeader>
          {venta && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={venta.fecha} data-testid="venta-fecha"
                       onChange={(e) => setVenta({ ...venta, fecha: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Método de pago</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={venta.metodo_pago} data-testid="venta-metodo"
                        onChange={(e) => setVenta({ ...venta, metodo_pago: e.target.value })}>
                  {["efectivo", "transferencia", "tarjeta", "cheque"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <LineasVenta lineas={venta.lineas} articulos={articulos}
                           onChange={(lineas) => setVenta({ ...venta, lineas })} />

              {hayFaltantes(venta.lineas) && (
                <label className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm sm:col-span-2">
                  <Checkbox checked={venta.permitir_negativo} data-testid="venta-permitir-negativo"
                            onCheckedChange={(v) => setVenta({ ...venta, permitir_negativo: !!v })} />
                  Vender igual aunque la existencia quede en negativo
                </label>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label>Estudiante o tutor (opcional)</Label>
                <BuscadorEstudiante estudiantes={estudiantes.filter((e) => e.estado === "activo")}
                                    valor={venta.estudiante_id} testId="venta-estudiante"
                                    onChange={(id) => setVenta({ ...venta, estudiante_id: id })} />
                {venta.estudiante_id ? (
                  <button className="text-xs text-primary hover:underline" data-testid="venta-quitar-estudiante"
                          onClick={() => setVenta({ ...venta, estudiante_id: "", facturar: false })}>
                    Quitar el estudiante (venta general)
                  </button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Busque por nombre del estudiante, matrícula, grado o nombre del tutor.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-3 text-sm sm:col-span-2">
                <Checkbox checked={venta.facturar} data-testid="venta-facturar"
                          onCheckedChange={(v) => setVenta({ ...venta, facturar: !!v })} />
                Emitir factura con NCF al tutor del estudiante
              </label>
              {venta.facturar && (
                <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4 sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>Comprobante fiscal (NCF)</Label>
                    <span className="font-mono text-xs text-muted-foreground">
                      Automático: {ncfSugerido || "—"}
                    </span>
                  </div>
                  <label className="flex items-center gap-3 text-sm">
                    <Checkbox checked={venta.ncf_manual} data-testid="venta-ncf-manual"
                              onCheckedChange={(v) => setVenta({ ...venta, ncf_manual: !!v })} />
                    Asignar el NCF manualmente
                  </label>
                  {venta.ncf_manual && (
                    <Input value={venta.ncf} placeholder={ncfSugerido || "B0100000001"} className="font-mono"
                           data-testid="venta-ncf" onChange={(e) => setVenta({ ...venta, ncf: e.target.value })} />
                  )}
                </div>
              )}
              <Button className="sm:col-span-2 rounded-full py-6" onClick={guardarVenta} data-testid="guardar-venta">
                Guardar venta
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!facturarVenta} onOpenChange={(o) => !o && setFacturarVenta(null)}>
        <DialogContent data-testid="modal-facturar-venta">
          <DialogHeader>
            <DialogTitle className="font-display">Emitir factura de la venta</DialogTitle>
            <DialogDescription>
              {facturarVenta?.venta.descripcion} · {money(facturarVenta?.venta.total || 0)}
            </DialogDescription>
          </DialogHeader>
          {facturarVenta && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Estudiante / tutor a facturar</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={facturarVenta.estudiante_id} data-testid="facturar-venta-estudiante"
                        onChange={(e) => setFacturarVenta({ ...facturarVenta, estudiante_id: e.target.value })}>
                  <option value="">Seleccione…</option>
                  {estudiantes.map((e) => <option key={e.id} value={e.id}>{`${e.nombre} — ${e.matricula}`}</option>)}
                </select>
              </div>
              <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Comprobante fiscal (NCF)</Label>
                  <span className="font-mono text-xs text-muted-foreground">Automático: {ncfSugerido || "—"}</span>
                </div>
                <label className="flex items-center gap-3 text-sm">
                  <Checkbox checked={facturarVenta.ncf_manual} data-testid="facturar-venta-ncf-manual"
                            onCheckedChange={(v) => setFacturarVenta({ ...facturarVenta, ncf_manual: !!v })} />
                  Asignar el NCF manualmente
                </label>
                {facturarVenta.ncf_manual && (
                  <Input value={facturarVenta.ncf} placeholder={ncfSugerido || "B0100000001"} className="font-mono"
                         data-testid="facturar-venta-ncf"
                         onChange={(e) => setFacturarVenta({ ...facturarVenta, ncf: e.target.value })} />
                )}
              </div>
              <Button className="rounded-full py-6" onClick={emitirFacturaVenta} data-testid="confirmar-facturar-venta">
                <Receipt className="mr-2 h-4 w-4" /> Emitir comprobante
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
